import { useApi } from "@shopexperts/shared";
import { useInfiniteQuery } from "@tanstack/vue-query";
import { computed, ref, type Ref, toValue, watch } from "vue";

export function useSchemaPages(schemaId: Ref<number> | number, limit: number = 20, search?: Ref<string> | string) {
  const api = useApi();

  const id = computed(() => toValue(schemaId));
  const searchTerm = computed(() => toValue(search)?.trim() || '');
  const debouncedSearchTerm = ref(searchTerm.value);

  watch(searchTerm, (value, _oldValue, onCleanup) => {
    const timeout = window.setTimeout(() => {
      debouncedSearchTerm.value = value;
    }, 250);
    onCleanup(() => window.clearTimeout(timeout));
  }, { immediate: true });

  const {
    data,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: computed(() => ['admin', 'pages', { schema_id: id.value, search: debouncedSearchTerm.value }]),
    queryFn: async ({ pageParam }) => {
      return await api.admin.pages.listPages({
        query: {
          schema_id: id.value,
          search: debouncedSearchTerm.value || undefined,
          limit,
          cursor: pageParam,
        },
      });
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined;
    },
    initialPageParam: undefined as string | undefined,
    enabled: computed(() => id.value > 0),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const pages = computed(() => {
    return data.value?.pages.flatMap(page => page.data) || [];
  });

  function loadMore() {
    if (hasNextPage.value && !isFetchingNextPage.value) {
      fetchNextPage();
    }
  }

  return {
    pages,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    loadMore,
    refetch,
  };
}
